import { Currencies, Currency } from "@/constants/currency";
import { IReceiptPaymentDetails } from "@/types/db/accounting";

export interface IBookingReceipt {
  products: IReceiptProduct[];
  groupInfo: IReceiptGroupInfo;
  bookingRef: string;
  discountTotal: string;
  netTotal: string;
  vat: string;
  vats: (IVatToPrint | null)[];
  total: string;
  date: string;
  hour: string;
  currency: string;
  payments: IReceiptPayment[];
  sellerName: string;
  paymentDetails: IReceiptPaymentDetails | null;
  taxTotal: string;

  isSignatureRequired?: boolean;
  isSigned?: boolean;
  orderName?: string;
  printVatLineLabels: boolean;

  orderSessionReference?: string;
  isReceiptCopy?: boolean;
  copyReceiptLabel?: string;

  // fiscal specific (AT)
  austriaAdditionalInfo?: IAustriaAdditionalInfo;
  // fiscal specific (DE)
  germanyAdditionalInfo?: IGermanyAdditionalInfo;
  // fiscal specific (IT)
  italyAdditionalInfo?: IItalyAdditionalInfo;
  // fiscal specific (ES)
  spainAdditionalInfo?: ISpainAdditionalInfo;
  // fiscal specific (FR)
  franceAdditionalInfo?: IFranceAdditionalInfo;
  // fiscal specific (HR)
  croatiaAdditionalInfo?: ICroatiaAdditionalInfo;
  // fiscal specific (BE)
  belgiumAdditionalInfo?: IBelgiumAdditionalInfo;

  /** Belgian receipts are scoped to one order session rather than the booking */
  sessionId?: string;
  remainingBalance?: string;

  isBill: boolean;
}

export interface IClosingReceipt {
  subTitle: string;
  groupInfo: IReceiptGroupInfo;
  currencies: Currency[];
  businessIdField: string;
  impressionDate: IKeyValue;
  openingDate: IKeyValue;
  closingDate: IKeyValue;
  cf: IKeyValue;
  seller: IKeyValue;
  message?: IKeyValue;
  messages: IKeyValues;
  initialCashFloats?: IKeyValues;
  totalCashSalesByCurrency?: IKeyValues;
  changeGivenBack?: IKeyValues;
  finalCashFloats?: IKeyValues;
  cashDifferences?: IKeyValues;

  /// Below fields are related to a receipt with cash transfers in it
  hasCashTransfersLogic?: boolean;
  ttlCashIn?: IKeyValue;
  ttlCashOut?: IKeyValue;
  expectedCashfloat?: IKeyValue;
  addedCashfloat?: IKeyValue;
  difference?: IKeyValue;

  // Fiskaly cash statement
  fiancialChannels?: string;
  fieldNet?: string;
  fieldGross?: string;
  fieldVatAmount?: string;
  amountsPerVat?: IAmountsPerVAT[];
}

export interface IAmountEntry {
  label: string;
  value: string;
  currency: string;

  isBold?: boolean;
  hasBorder?: boolean;
  marginBottom?: boolean;
  marginTop?: boolean;
}
export interface IPaymentMethodSection {
  title: string;
  lines: IAmountEntry[];
}

export interface IReceiptEntry {
  value: string;
  isBold?: boolean;
}

export interface IReceiptLine {
  label: string;
  columns: IReceiptEntry[];
  isBold?: boolean;
  hasBorder?: boolean;
}

export interface IReceiptSection {
  headers: string[];
  lines: IReceiptLine[];
}
export interface IPaymentSummary {
  title: string;
  headers: string[];
  sections: IReceiptSection[];
}

export interface ICashPaymentSummary {
  title: string;
  headers: string[];
  lines: IReceiptLine[];
}

export interface IClosingSummaryReceipt {
  cashfloatUid: IKeyValue;
  groupInfo: IReceiptGroupInfo;
  businessId: IKeyValue;
  printDate: IKeyValue;
  openingDate: IKeyValue;
  closingDate: IKeyValue;
  openendBy: IKeyValue;
  closedBy: IKeyValue;
  device?: IKeyValue;

  paymentSummary: IPaymentSummary;
  paymentMethodSections: IPaymentMethodSection[];

  closureDetailsField: string;
  reconciliationOverviewField: string;
  salesDetailsField: string;
}

export interface IVat {
  name: string;
  rate: number;
  amount: number;
  amountExclVat: number;
  amountInclVat: number;
}

export interface IVatToPrintNum {
  name: string;
  rate: string; // e.g: 5%
  vatAmount: number; // 5.50
  amountExclVat: number; // 5.50
  amountInclVat: number; // 5.50
  // priceId: number;
  priceId: number[];
  vatIndex: string;
  type: VatLineType;
}

export interface IVatToPrint {
  name: string;
  rate: string; // e.g: 5%
  vatAmount: string; // 5.50
  amountExclVat: string; // 5.50
  amountInclVat: string; // 5.50
  // priceId: number;
  priceId: number[];
  vatIndex: string;
  type: VatLineType;
}

export interface IReceiptProduct {
  name: string;
  priceName: string;
  startDateTime: string;
  quantity: number;
  priceValue: string;
  hasCancelledTickets?: boolean;
  quantityCancelled?: number;
  cancellationVatIndex?: string;
  valueCancelled?: string;
  discountValue?: string;
  discountPercentage?: string;
  /** Gross unit price — rendered next to the quantity on the receipt line. */
  unitPrice?: string;
  /** Cross-reference to the receipt's VAT summary line (e.g. "1"). */
  vatIndex?: string;
}

export interface IReceiptGroupInfo {
  name: string;
  address: string;
  registrationNum: string;
  vatNumber: string;
  phone: string;
  country?: string;
  exoNumber?: string;
  vatId?: string;
  vatOnPayment?: boolean;
  label?: string;
  companyName?: string;
  cif?: string;
  region?: string;
  province?: string;
  city?: string;
  zip?: string;
  displayName?: string;
  siret?: string;
  nafCode?: string;
  siren?: string;
  rcs?: string;
  legalForm?: string;
  socialCapital?: string;
}

export interface IReceiptPayment {
  name: string;
  providedCashBill?: string;
  exchangeRate?: string;
  value: string;
  currency: string;
}

export enum PrintTemplates {
  BookingReceipt = "booking-receipt",
  BookingTicket = "booking-ticket",
  BookingMiniTicket = "booking-mini-ticket",
  BookingLabel = "booking-label",
  VHSTicket = "vhs-ticket",
  EdeisTicket = "edeis-ticket",
  OrderSummaryReceipt = "order-summary-receipt",
  ClosingReceipt = "closing-receipt",
  ClosingSummaryReceipt = "closing-summary-receipt",
}

export interface IKeyValue {
  key: string;
  value: string;
}

export interface IKeyValues {
  key: string;
  values: Partial<Record<Currencies, string>>;
}

export interface ITicketProduct {
  productName: string;
  categoryName: string;
  subCategoryName?: string;
  address: string;
  priceName: string;
  startDateTime: string;
  endDateTime: string;
  printDateTime?: string;
  productCustomText?: string;
  priceValue: string;
  discountValue?: string;
  qrcode: string;
  isIdivTax: number;
  seat?: string;
  voucherCode?: string;
  productLogo?: string;
  isGiftCard?: boolean;
  isMembership?: boolean;
  multiDayTicket?: boolean;
  barCode?: string;
  ticketLogo?: string;
}
export interface IBookingTicket {
  groupLogoBase64?: string;
  tickets: ITicketProduct[];
  groupInfo: IReceiptGroupInfo;
  bookingRef: string;
  currency: string;
  fieldDiscount: string;
  fieldReference: string;
  fieldTicket: string;
  fieldPaid: string;
  fieldFrom: string;
  fieldTo: string;
  fieldPrice: string;
  fieldSeat?: string;
  fieldPrintDateTime?: string;
  fieldVHSTicketTax?: string;
  fieldVHSTicketTerms?: string;
  fieldValidUntil?: string;
}

export interface IBookingMiniTicket {
  groupLogoBase64?: string;
  groupInfo: IReceiptGroupInfo;
  bookingRef: string;
  currency: string;
  name: string;
  productName: string;
  priceName: string;
  subCategoryName?: string;
  startDateTime: string;
  endDateTime: string;
  printDateTime?: string;
  productCustomText?: string;
  priceValue: string;
  qrCode: string;
  seat?: string;
  fieldReference: string;
  fieldTicket: string;
  fieldPaid: string;
  fieldFrom: string;
  fieldTo: string;
  fieldPrice: string;
  fieldSeat?: string;
  fieldPrintDateTime?: string;
  voucherCode?: string;
  productLogo?: string;
  fieldVHSTicketTax?: string;
  fieldVHSTicketTerms?: string;
  isGiftCard?: boolean;
  isMembership?: boolean;
  multiDayTicket?: boolean;
  fieldValidUntil?: string;
  barCode?: string;
  ticketLogo?: string;
}

export enum TicketTypes {
  Ticket = "ticket",
  MiniTicket = "mini-ticket",
  EdeisLarge = "edeis-large", // Boca ticket 101.6mm x 54mm
  EdeisSmall = "edeis-small", // Boca ticket 84.1mm x 53.1mm
}

export interface IBookingLabel {
  date: string;
  startTime: string;
  endTime: string;
  ticketLogo?: string;
}

export interface IAmountsPerVAT {
  vatRate: string;
  vatAmount: string;
  net: string;
  gross: string;
  currency: Currencies;
}

export enum LabelTypes {
  FlipLab = "fliplab",
  Jumpark = "jumpark",
}

export enum VatLineType {
  Cancellation,
  Purchase,
}

export interface IFranceAdditionalInfo {
  isReprint: boolean;
  /** Raw EFR-signed sale time ("YYYY-MM-DD HH:mm:ss") for @smeetz/receipt. */
  efrSaleTimestamp?: string;
  signature?: string;
  originalSignature?: string;
  fiscalNumber?: string;
  originalFiscalNumber?: string;
  nafCode?: string;
  siret?: string;
  siren?: string;
  socialCapital?: string;
  legalForm?: string;
  documentTypexOperation?: string;
  swVersion?: string;
  swVersionOriginal?: string; // reprint only
  posId?: string;
  lineCount?: string;
  printCount?: string;
  clientId?: string;
  client?: string;
  clientAddress?: string;
  reprintTimestamp?: string; // reprint only
  posIdOriginal?: string; // reprint only
  sellerName?: string;
  originalSellerName?: string;
}

export interface ISpainAdditionalInfo {
  isReprint: boolean;
  qrCode?: string;
  fiscalNumber?: string;
  timestamp?: string;
  signature?: string;
}

export interface IGermanyAdditionalInfo {
  isReprint: boolean;
  qrCode?: string;
  fiscalNumber: any;
  timestamp: string;
  signature: any;
}

export interface IAustriaAdditionalInfo {
  qrCode: string;
  receiptType?: string;
  receiptNumber: string;
  posId: string;
  fiskalyCashRegisterId: string;
}

export interface IItalyAdditionalInfo {
  signature: string;
  documentId: string;
  receiptType?: string;
}

export interface ICroatiaAdditionalInfo {
  isReprint: boolean;
  qrCode?: string;
  oib?: string;
  timestamp?: string;
  fiscalNumber?: string;
  zki?: string;
  jir?: string;
  pkp?: string;
  customerName?: string;
  customerAddress?: string;
}

/**
 * Belgian FDM data printed on a receipt.
 *
 * Note this describes the values actually rendered; the labels come from i18n
 * via the `t` handlebars helper rather than being carried on this object.
 */
export interface IBelgiumAdditionalInfo {
  isReprint: boolean;
  /** only an original sale is a valid VAT receipt; a copy is not */
  isValidVatReceipt: boolean;
  isFullCancellation: boolean;
  /** copies and cancellations also print the referenced original's FDM ref */
  includeOriginalFdmRef: boolean;

  qrCode?: string;
  signature?: string;
  /** signature of the original sale, printed alongside the copy's own */
  originalSignature?: string;
  fiscalNumber?: string;
  /** fiscal number of the original sale, printed on a copy */
  originalFiscalNumber?: string;

  // FDM identifiers
  fdmId?: string;
  fdmDateTime?: string;
  ticketCounter?: string;
  originalFdmId?: string;
  originalFdmDateTime?: string;
  originalTicketCounter?: string;

  // POS / terminal identity
  swVersion?: string;
  posId?: string;
  terminalId?: string;
  deviceId?: string;
  transactionNumber?: number;
  fdmVersion?: string;
  posDateTime?: string;
  /** establishment unit number */
  locationCbe?: string;
  footer?: string;

  // operator and customer
  sellerName?: string;
  sellerId?: string | number;
  clientId?: string;
  client?: string;
  reprintTimestamp?: string;
}
